import { useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import {
  OrderStatus,
  UpdateOrderDetailsEventAndTypeMutation,
} from '__generated__/atlas-types'

import {
  isEditApprovedShippedRestricted as isEditApprovedShippedRestrictedSelector,
  isLiteRetailer as isLiteRetailerSelector,
  isProBrand as isProBrandSelector,
} from 'store/currentUser/selectors'

import { OrderDetailsQuery } from './OrderDetailsWidget'
import {
  useUpdateEventAndType,
  useUpdateWarehouseAndSeason,
} from './orderDetailsWidget.mutations'
import {
  FormValues,
  HandleChangeFunction,
  PayloadParams,
  UpdateOrderDetailsResult,
  formUtils,
  updateDetailsUtils,
} from './orderDetailsWidget.utils'
import { useOrderId } from 'features/Orders/orders.hooks'

export function useCanEditDetails(orderStatus: OrderStatus | undefined) {
  const isProBrand = useSelector(isProBrandSelector)
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  const isEditApprovedShippedRestricted = useSelector(
    isEditApprovedShippedRestrictedSelector,
  )
  const [isEditing, setIsEditing] = useState(false)

  const isApprovedOrShipped =
    orderStatus === OrderStatus.APPROVED || orderStatus === OrderStatus.SHIPPED

  return {
    canEditDetails:
      !!orderStatus &&
      (Boolean(
        isProBrand &&
        (!isEditApprovedShippedRestricted || !isApprovedOrShipped),
      ) ||
        (isLiteRetailer && orderStatus === OrderStatus.NOTES)),
    isEditing,
    setIsEditing,
  }
}

export function useOrderDetailsForm(
  isEditing: boolean,
  details?: OrderDetailsQuery,
) {
  const [formValues, setFormValues] = useState<FormValues>({} as FormValues)

  useEffect(() => {
    if (!isEditing) {
      setFormValues(formUtils.getFormValues(details) as FormValues)
    }
  }, [details, isEditing])

  const handleFormChange: HandleChangeFunction = (id, value) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: value,
    }))
  }

  const resetForm = () =>
    setFormValues(formUtils.getFormValues(details) as FormValues)

  return {
    formValues,
    handleFormChange,
    resetForm,
  }
}

export function useUpdateOrderDetails() {
  const orderId = useOrderId()

  const [mutateEventAndType, { loading: updatingEventAndType }] =
    useUpdateEventAndType()

  const [mutateWarehouseAndSeason, { loading: updatingWarehouseAndSeason }] =
    useUpdateWarehouseAndSeason()

  const updateOrderDetails = async (
    payload: PayloadParams,
  ): Promise<UpdateOrderDetailsResult> => {
    const { inputEventAndType, inputDeliveryInfo } =
      updateDetailsUtils.formatMutationInputs(payload, orderId)

    const promises = [
      ...(inputEventAndType !== null
        ? [
            mutateEventAndType({
              variables: {
                orderInfoInput: {
                  ...inputEventAndType,
                },
              },
            }),
          ]
        : []),
      ...(inputDeliveryInfo !== null
        ? [
            mutateWarehouseAndSeason({
              variables: {
                input: {
                  ...inputDeliveryInfo,
                },
              },
            }),
          ]
        : []),
    ]

    if (promises.length === 0) {
      return updateDetailsUtils.errorUpdateResult
    }

    return Promise.allSettled(promises)
      .then(async (results) => {
        if (!results.length) {
          return updateDetailsUtils.errorUpdateResult
        }

        const resultsAgg =
          updateDetailsUtils.initializeResultsAggregation(payload)

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            const orderDetailsResponse = result?.value
              ?.data as UpdateOrderDetailsEventAndTypeMutation

            if (
              inputEventAndType !== null &&
              orderDetailsResponse?.updateOrderInfo?.errors?.length
            ) {
              resultsAgg['updateOrderInfo'].failed = true
            } else {
              const { value } = result
              const errors = value?.errors

              if (errors?.length) {
                const { path } = errors[0]
                if (path) {
                  const [mutationName] = path
                  resultsAgg[mutationName].failed = true
                }
              }
            }
          }
        })

        return updateDetailsUtils.computeResult({
          resultsAgg,
          mutationCount: results.length,
        })
      })
      .catch((_err) => updateDetailsUtils.errorUpdateResult)
  }

  return {
    mutationsLoading: updatingEventAndType || updatingWarehouseAndSeason,
    updateOrderDetails,
  }
}
